/**
 * dashboard-api — typowane fetchery kafelków pulpitu (`/api/dashboard/*`).
 *
 * Wszystkie kafelki z danymi liczone/listowane są w kontekście aktywnej firmy
 * (`X-Company-Id` dopina interceptor `lib/api.ts`) lub tenanta (KPI „firmy").
 * Tryb „Wszystkie firmy" idzie parametrem `scope=all` (suma/lista po firmach usera).
 * Backend zwraca 400 `MISSING_HEADER` gdy brak aktywnej firmy i 403 gdy brak
 * uprawnienia — `classifyDataError()` mapuje oba na łagodny stan kafelka zamiast
 * wywrotki przez `WidgetErrorBoundary`.
 */

import type { AxiosError } from 'axios';

import { api } from '@/lib/api';

import type {
  BreakdownDimension,
  ChartBucket,
  CompanyEntity,
  DashboardEntity,
  DashboardPeriod,
  KpiMetric,
  KpiUnit,
} from './types';

export type DataScope = 'company' | 'all';

const scopeParam = (scope: DataScope) => (scope === 'all' ? { scope } : {});

export interface KpiResponse {
  metric: KpiMetric;
  period: DashboardPeriod;
  count: number;
  /** Ta sama metryka w poprzednim oknie czasu; `null`/brak = nie dotyczy (okres „cały"). */
  previous?: number | null;
  /** Jednostka wartości: liczba, kwota (PLN) albo godziny. Brak = liczba. */
  unit?: KpiUnit;
  /** Z ilu firm złożona jest suma — tylko przy `scope: 'all'`. */
  companies?: number;
}

/**
 * `scope: 'all'` = suma po wszystkich firmach użytkownika (tryb „Wszystkie firmy"), bez nagłówka
 * aktywnej firmy; domyślnie liczy aktywną firmę.
 */
export async function fetchKpi(
  metric: KpiMetric,
  period: DashboardPeriod,
  scope: DataScope = 'company',
): Promise<KpiResponse> {
  const res = await api.get<KpiResponse>('/dashboard/kpi', {
    params: { metric, period, ...scopeParam(scope) },
  });
  return res.data;
}

export interface ChartSeriesPoint {
  bucket: string;
  count: number;
}

export interface ChartCompanySeries {
  companyId: string;
  name: string;
  series: ChartSeriesPoint[];
  previousSeries?: ChartSeriesPoint[];
}

export interface ChartResponse {
  entity: DashboardEntity;
  period: DashboardPeriod;
  bucket: ChartBucket;
  /** Seria łączna (przy `scope=all` — suma po firmach). */
  series: ChartSeriesPoint[];
  /** Suma serii (liczba albo kwota — zależnie od `measure`). */
  total?: number;
  /** Przy `compare=1`: seria poprzedniego okna przesunięta na oś bieżącego okna. */
  previousSeries?: ChartSeriesPoint[];
  previousTotal?: number;
  /** Tylko przy `scope=all`: seria osobno dla każdej firmy (wykres skumulowany). */
  byCompany?: ChartCompanySeries[];
}

export interface ChartQuery {
  entity: DashboardEntity;
  period: DashboardPeriod;
  bucket: ChartBucket;
  scope?: DataScope;
  /** `amount` = suma brutto (tylko faktury); domyślnie liczba rekordów. */
  measure?: 'count' | 'amount';
  /** Dołącz serię poprzedniego okna (porównanie rok do roku itp.). */
  compare?: boolean;
}

export async function fetchChart(q: ChartQuery): Promise<ChartResponse> {
  const res = await api.get<ChartResponse>('/dashboard/chart', {
    params: {
      entity: q.entity,
      period: q.period,
      bucket: q.bucket,
      ...scopeParam(q.scope ?? 'company'),
      ...(q.measure === 'amount' ? { measure: 'amount' } : {}),
      ...(q.compare ? { compare: 1 } : {}),
    },
  });
  return res.data;
}

export interface BreakdownItem {
  /** Klucz grupy: id firmy (wymiar `company`) albo kod statusu (wymiar `status`). */
  key?: string;
  companyId?: string;
  name: string;
  count: number;
}

export interface BreakdownResponse {
  metric: DashboardEntity;
  period: DashboardPeriod;
  total: number;
  items: BreakdownItem[];
}

/** Rozkład metryki po firmach (domyślnie) albo statusach użytkownika (dane wykresu kołowego). */
export async function fetchBreakdown(
  metric: DashboardEntity,
  period: DashboardPeriod,
  dimension: BreakdownDimension = 'company',
  scope: DataScope = 'company',
): Promise<BreakdownResponse> {
  const res = await api.get<BreakdownResponse>('/dashboard/breakdown', {
    params: {
      metric,
      period,
      ...(dimension === 'status' ? { dimension, ...scopeParam(scope) } : {}),
    },
  });
  return res.data;
}

export interface FlowPoint {
  bucket: string;
  created: number;
  completed: number;
}

export interface FlowResponse {
  period: DashboardPeriod;
  bucket: ChartBucket;
  series: FlowPoint[];
}

/** Dokumenty „Nowe vs Zakończone" w czasie (słupki grupowe). */
export async function fetchFlow(
  period: DashboardPeriod,
  bucket: ChartBucket,
  scope: DataScope = 'company',
): Promise<FlowResponse> {
  const res = await api.get<FlowResponse>('/dashboard/flow', {
    params: { period, bucket, ...scopeParam(scope) },
  });
  return res.data;
}

export interface ProcessPerformanceRow {
  processId: string;
  name: string;
  onTimePct: number;
  total: number;
}

export interface ProcessPerformanceResponse {
  processes: ProcessPerformanceRow[];
  gauges: { onTimePct: number; completedPct: number };
}

export async function fetchProcessPerformance(
  period: DashboardPeriod,
  scope: DataScope = 'company',
): Promise<ProcessPerformanceResponse> {
  const res = await api.get<ProcessPerformanceResponse>('/dashboard/process-performance', {
    params: { period, ...scopeParam(scope) },
  });
  return res.data;
}

export type TransactionStatus = 'booked' | 'paid' | 'pending' | 'draft' | 'rejected';

export interface TransactionRow {
  id: string;
  number: string | null;
  title: string | null;
  counterparty: string | null;
  companyName: string | null;
  date: string;
  /** Kwota brutto; zakup ma znak ujemny. */
  amountGross: number | null;
  currency: string | null;
  direction: 'in' | 'out';
  status: TransactionStatus;
  href: string | null;
}

export async function fetchTransactions(
  limit: number,
  scope: DataScope = 'company',
): Promise<TransactionRow[]> {
  const res = await api.get<{ items: TransactionRow[] }>('/dashboard/transactions', {
    params: { limit, ...scopeParam(scope) },
  });
  return res.data.items;
}

export type AlertSeverity = 'info' | 'warning' | 'success' | 'error';

export interface AlertRow {
  id: string;
  severity: AlertSeverity;
  title: string;
  description: string;
  at: string;
  href: string | null;
  unread: boolean;
}

export async function fetchAlerts(scope: DataScope = 'company'): Promise<AlertRow[]> {
  const res = await api.get<{ items: AlertRow[] }>('/dashboard/alerts', {
    params: { ...scopeParam(scope) },
  });
  return res.data.items;
}

export interface ProfileStats {
  companies: number;
  documents: number;
  lastLoginAt: string | null;
}

export async function fetchProfileStats(scope: DataScope = 'company'): Promise<ProfileStats> {
  const res = await api.get<ProfileStats>('/dashboard/profile-stats', {
    params: { ...scopeParam(scope) },
  });
  return res.data;
}

export interface ActivityRow {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  /** Czytelna etykieta rekordu (np. numer dokumentu), jeśli wpis ją niesie. */
  label?: string | null;
  createdAt: string;
}

export async function fetchActivity(limit: number): Promise<ActivityRow[]> {
  const res = await api.get<{ data: ActivityRow[] }>('/dashboard/activity', {
    params: { limit },
  });
  return res.data.data;
}

export interface RecentRow {
  id: string;
  label: string;
  createdAt: string;
}

export async function fetchRecent(entity: CompanyEntity, limit: number): Promise<RecentRow[]> {
  const res = await api.get<{ entity: CompanyEntity; data: RecentRow[] }>('/dashboard/recent', {
    params: { entity, limit },
  });
  return res.data.data;
}

/**
 * Klasyfikuje błąd zapytania kafelka danych. Backend zwraca:
 *   - 400 `MISSING_HEADER` / 403 `NO_ACCESS` → brak aktywnej firmy.
 *   - 403 `PERMISSION_DENIED` → brak uprawnienia do metryki.
 * Reszta (sieć, 500) → `error` (kafelek pokazuje retry przez boundary/inline).
 */
export type DataWidgetErrorKind = 'no-company' | 'permission' | 'error';

export function classifyDataError(err: unknown): DataWidgetErrorKind {
  const axiosErr = err as AxiosError<{ error?: string }>;
  const status = axiosErr?.response?.status;
  const code = axiosErr?.response?.data?.error;
  if (status === 403 && code === 'PERMISSION_DENIED') return 'permission';
  if (code === 'MISSING_HEADER' || code === 'INVALID_UUID' || code === 'NO_ACCESS') {
    return 'no-company';
  }
  return 'error';
}
